import { IDisposable, toDisposable, Disposable } from '@vscode-alt/monaco-editor/esm/vs/base/common/lifecycle';
import { TypeConstraint, validateConstraints } from '@vscode-alt/monaco-editor/esm/vs/base/common/types';
import { Event, Emitter } from '@vscode-alt/monaco-editor/esm/vs/base/common/event';
import { LinkedList } from '@vscode-alt/monaco-editor/esm/vs/base/common/linkedList';
// import { Iterable } from '@vscode-alt/monaco-editor/esm/vs/base/common/iterator';
import { ICommandRegistry, ICommandHandler, ICommandsMap, ICommandsService, ICommand } from '@workbench-stack/core';
import { Registry } from '@vscode-alt/monaco-editor/esm/vs/platform/registry/common/platform';
import { Iterable } from '@vscode-alt/monaco-editor/esm/vs/base/common/iterator';

export class CommandsRegistry implements ICommandRegistry {

    private readonly _commands = new Map<string, LinkedList<ICommand>>();

    private readonly _onDidRegisterCommand = new Emitter<string>();
    public readonly onDidRegisterCommand: Event<string> = this._onDidRegisterCommand.event;

    public registerCommand(idOrCommand: string | ICommand, handler?: ICommandHandler): IDisposable {

        if (!idOrCommand) {
            throw new Error(`invalid command`);
        }

        if (typeof idOrCommand === 'string') {
            if (!handler) {
                throw new Error(`invalid command`);
            }
            return this.registerCommand({ id: idOrCommand, handler });
        }

        // add argument validation if rich command metadata is provided
        if (idOrCommand.description) {
            const constraints: Array<TypeConstraint | undefined> = [];
            for (let arg of idOrCommand.description.args) {
                constraints.push(arg.constraint);
            }
            const actualHandler = idOrCommand.handler;
            idOrCommand.handler = function (accessor, ...args: any[]) {
                validateConstraints(args, constraints);
                return actualHandler(accessor, ...args);
            };
        }

        // find a place to store the command
        const { id } = idOrCommand;

        let commands = this._commands.get(id);
        if (!commands) {
            commands = new LinkedList<ICommand>();
            this._commands.set(id, commands);
        }

        let removeFn = commands.unshift(idOrCommand);

        let ret = toDisposable(() => {
            removeFn();
            const command = this._commands.get(id);
            if (command?.isEmpty()) {
                this._commands.delete(id);
            }
        });

        // tell the world about this command
        this._onDidRegisterCommand.fire(id);

        return ret;
    }

    public registerCommandAlias(oldId: string, newId: string): IDisposable {
        // return CommandsRegistry.registerCommand(oldId, (accessor, ...args) => accessor.get(ICommandsService).executeCommand(newId, ...args));
        return this.registerCommand(oldId, (accessor, ...args) => accessor.get(ICommandsService).executeCommand(newId, ...args));
    }

    public getCommand(id: string): ICommand | undefined {
        const list = this._commands.get(id);
        if (!list || list.isEmpty()) {
            return undefined;
        }
        return Iterable.first(list);
    }

    public getCommands(): ICommandsMap {
        const result = new Map<string, ICommand>();
		for (const key of this._commands.keys()) {
			const command = this.getCommand(key);
			if (command) {
				result.set(key, command);
			}
		}
        return result;
    }
}
const Extensions = {
    CommandsRegistry: 'workbench.registry.commands',
};

Registry.add(Extensions.CommandsRegistry, new CommandsRegistry());
